import { useEffect, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { PlusIcon, MagnifyingGlassIcon, PencilSimpleIcon, TrashIcon, XIcon, WhatsappLogoIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { whatsappLink } from "@/lib/whatsapp";

function ClientForm({ initial, onSave, onCancel }) {
  const [data, setData] = useState(
    initial || { name: "", phone: "", document: "", email: "", address: "", notes: "" }
  );
  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    await onSave(data);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="client-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldText label="Nome *" value={data.name} onChange={set("name")} required testid="client-name-input" />
        <FieldText label="Telefone" value={data.phone} onChange={set("phone")} testid="client-phone-input" />
        <FieldText label="CPF/CNPJ" value={data.document} onChange={set("document")} testid="client-doc-input" />
        <FieldText label="E-mail" value={data.email} onChange={set("email")} testid="client-email-input" />
      </div>
      <FieldText label="Endereço" value={data.address} onChange={set("address")} testid="client-address-input" />
      <FieldTextarea label="Observações" value={data.notes} onChange={set("notes")} testid="client-notes-input" />
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 transition" data-testid="client-cancel-btn">
          Cancelar
        </button>
        <button type="submit" className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium shadow-[0_0_15px_rgba(37,99,235,0.4)] transition" data-testid="client-save-btn">
          Salvar
        </button>
      </div>
    </form>
  );
}

export function FieldText({ label, value, onChange, type = "text", required, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">{label}</span>
      <input
        type={type}
        value={value || ""}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition"
        {...rest}
      />
    </label>
  );
}

export function FieldTextarea({ label, value, onChange, testid, rows = 3 }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">{label}</span>
      <textarea
        value={value || ""}
        onChange={onChange}
        rows={rows}
        data-testid={testid}
        className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition resize-y"
      />
    </label>
  );
}

export function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-slate-400 mb-1.5">{label}</span>
      <select
        value={value || ""}
        onChange={onChange}
        data-testid={testid}
        className="w-full bg-black/20 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-slate-900">
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Drawer({ open, title, onClose, children, size = "md" }) {
  if (!open) return null;
  const widths = { md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative ml-auto w-full ${widths[size]} h-full bg-slate-950 border-l border-white/10 overflow-y-auto`}>
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/10 bg-slate-950/80 backdrop-blur">
          <h2 className="font-display text-xl font-semibold">{title}</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 transition" data-testid="drawer-close">
            <XIcon size={18} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function Clients() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/clients", { params: { q } });
    setItems(data);
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const save = async (data) => {
    try {
      if (editing?.id) {
        await api.put(`/clients/${editing.id}`, data);
        toast.success("Cliente atualizado");
      } else {
        await api.post("/clients", data);
        toast.success("Cliente criado");
      }
      setOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Erro ao salvar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este cliente?")) return;
    await api.delete(`/clients/${id}`);
    toast.success("Cliente removido");
    load();
  };

  return (
    <div data-testid="clients-page">
      <PageHeader
        title="Clientes"
        subtitle="Cadastro completo da sua base de clientes."
        actions={
          <button
            onClick={() => { setEditing(null); setOpen(true); }}
            data-testid="new-client-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white font-medium px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-[0_0_15px_rgba(37,99,235,0.4)] transition hover:-translate-y-0.5"
          >
            <PlusIcon size={16} weight="bold" /> Novo cliente
          </button>
        }
      />

      <div className="glass rounded-xl p-6">
        <div className="relative max-w-md mb-5">
          <MagnifyingGlassIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="clients-search"
            placeholder="Buscar por nome, telefone, CPF/CNPJ..."
            className="w-full bg-black/20 border border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 outline-none transition"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5 sticky top-0 backdrop-blur">
              <tr>
                <th className="py-3 pr-4">Nome</th>
                <th className="py-3 pr-4">Telefone</th>
                <th className="py-3 pr-4">CPF/CNPJ</th>
                <th className="py-3 pr-4">E-mail</th>
                <th className="py-3 pr-4">Cadastrado</th>
                <th className="py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/5 transition" data-testid={`client-row-${c.id}`}>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-emerald-500 flex items-center justify-center text-xs font-bold">
                        {c.name?.[0]?.toUpperCase()}
                      </div>
                      <span className="font-medium">{c.name}</span>
                    </div>
                  </td>
                  <td className="py-3 pr-4 text-slate-400">{c.phone || "—"}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-slate-400">{c.document || "—"}</td>
                  <td className="py-3 pr-4 text-slate-400">{c.email || "—"}</td>
                  <td className="py-3 pr-4 text-slate-500">{fmtDate(c.created_at)}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {c.phone && (
                        <a
                          href={whatsappLink(c.phone, `Olá, ${c.name?.split(" ")[0] || ""}!`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-emerald-400 transition"
                          title="WhatsApp"
                          data-testid={`whatsapp-client-${c.id}`}
                        >
                          <WhatsappLogoIcon size={16} weight="duotone" />
                        </a>
                      )}
                      <button
                        onClick={() => { setEditing(c); setOpen(true); }}
                        className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-blue-400 transition"
                        data-testid={`edit-client-${c.id}`}
                      >
                        <PencilSimpleIcon size={16} />
                      </button>
                      <button
                        onClick={() => remove(c.id)}
                        className="p-2 rounded-md hover:bg-white/10 text-slate-400 hover:text-rose-400 transition"
                        data-testid={`delete-client-${c.id}`}
                      >
                        <TrashIcon size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500">
                    Nenhum cliente encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Drawer
        open={open}
        title={editing ? "Editar cliente" : "Novo cliente"}
        onClose={() => { setOpen(false); setEditing(null); }}
      >
        <ClientForm initial={editing} onSave={save} onCancel={() => { setOpen(false); setEditing(null); }} />
      </Drawer>
    </div>
  );
}
